import EmptyPart from "./editors/EmptyPart.vue";
import BodyPart from "./containers/BodyPart.vue";
import BlockPart from "./containers/BlockPart.vue";
import ColumnsPart from "./containers/ColumnsPart.vue";
import ResponsivePart from "./containers/ResponsivePart.vue";
import WidgetsPart from "./containers/WidgetsPart.vue";
import WidgetPart from "./containers/WidgetPart.vue";
import FlowPart from "./containers/FlowPart.vue";
import DetailsPart from "./containers/DetailsPart.vue";
import TabsPart from "./containers/TabsPart.vue";
import ButtonPart from "./editors/ButtonPart.vue";
import TextPart from "./editors/TextPart.vue";
import LinkPart from "./editors/LinkPart.vue";
import HtmlPart from "./editors/HtmlPart.vue";
import InputPart from "./editors/InputPart.vue";
import TextAreaPart from "./editors/TextAreaPart.vue";
import HtmlAreaPart from "@fd/editors/html/HtmlAreaPart.vue";
import CheckboxPart from "./editors/CheckboxPart.vue";
import SelectPart from "./editors/SelectPart.vue";
import SelectionPart from "./editors/SelectionPart.vue";
import SuggesterPart from "./editors/suggester/SuggesterPart.vue";
import TemporalPart from "./editors/temporal/TemporalPart.vue";
import StaticDateTimePart from "./editors/temporal/StaticDateTimePart.vue";
import SwitchPart from "./editors/SwitchPart.vue";
import NoticePart from "./helpers/NoticePart.vue";
import UserGroupPart from "./editors/UserGroupPart.vue";
import SplitPart from "./containers/SplitPart.vue";
import EAddressPart from "./editors/eaddress/EAddressPart.vue";
import ArrayPart from "./containers/ArrayPart.vue";
import AddressPart from "./editors/address/AddressPart.vue";
import MemberPartyPart from "./editors/member/MemberPartyPart.vue";
import type { FormPartOrReference } from "../types/fd/FormPartOrReference";
import type { FormPart as FormPartType } from "../types/fd/FormPart";
import type { Notice } from "../types/fd/fixed/Notice";
import type { Empty } from "../types/fd/fixed/Empty";
import TouchPart from "./containers/TouchPart.vue";
import DialogPart from "./containers/DialogPart.vue";
import RadioPart from "./editors/RadioPart.vue";
import MaskedPart from "./containers/MaskedPart.vue";
import JoinMaskedPart from "@fd/containers/JoinMaskedPart.vue";
import UnmaskPart from "./editors/UnmaskPart.vue";
import CatsPart from "./editors/CatsPart.vue";
import IconPart from "./editors/IconPart.vue";
import ImagePart from "./editors/ImagePart.vue";
import GridPart from "./containers/GridPart.vue";
import ConsentPart from "./editors/ConsentPart.vue";
import MemberPartiesPart from "./editors/member/MemberPartiesPart.vue";
import RelationsPart from "./editors/relation/RelationsPart.vue";
import RelationPart from "./editors/relation/RelationPart.vue";
import ChoosePart from "./containers/ChoosePart.vue";
import AlwaysVisiblePart from "./containers/AlwaysVisiblePart.vue";
import MoneyPart from "./editors/MoneyPart.vue";
import EventColorPart from "./editors/EventColorPart.vue";
import AgendaPart from "./containers/AgendaPart.vue";
import FilterPart from "./editors/filter/FilterPart.vue";
import FilterItemPart from "./editors/filter/FilterItemPart.vue";
import MenuPart from "./editors/MenuPart.vue";
import ModPart from "@fd/editors/ModPart.vue";
import ChecklistPart from "./editors/ChecklistPart.vue";
import FieldsetPart from "./containers/FieldsetPart.vue";
import DataWidgetsPart from "./containers/DataWidgetsPart.vue";
import VariantPart from "@fd/containers/VariantPart.vue";
import MentionsPart from "./editors/mention/MentionsPart.vue";
import FileEntryPart from "./editors/file/FileEntryPart.vue";
import FileZonePart from "./editors/file/FileZonePart.vue";
import AvatarPart from "./editors/file/AvatarPart.vue";
import TypeAttachPart from "./editors/TypeAttachPart.vue";
import IntlInputPart from "./editors/IntlInputPart.vue";
import IntlTextAreaPart from "./editors/IntlTextAreaPart.vue";
import ViewColumnsPart from "@fd/editors/view/ViewColumnsPart.vue";
import WidgetGroupPart from "@fd/containers/WidgetGroupPart.vue";
import ReferenceHeaderPart from "@fd/editors/reference/ReferenceHeaderPart.vue";
import ReferencePart from "@fd/editors/reference/ReferencePart.vue";
import ReferencesPart from "@fd/editors/reference/ReferencesPart.vue";
import EventRecurrencePart from "@fd/editors/EventRecurrencePart.vue";
import {CustomPart} from "@t/fd/CustomPart.ts";
import {InterfaceRoot, RootLayout} from "@t/fd/InterfaceRoot.ts";
import FormPart from "@fd/containers/FormPart.vue";
import ItemsPart from "@fd/containers/ItemsPart.vue";
import UserSelectPart from "@fd/editors/user-select/UserSelectPart.vue";


type PartRefFunction = (part: FormPartOrReference) => FormPartType;

export function partRef(part: FormPartOrReference, root: InterfaceRoot<RootLayout>): FormPartType {
	if (typeof part === "object") return part; // already resolved

	// check definitions
	if (root.define) {
		let resolved: FormPartOrReference = part;
		do {
			resolved = root.define[resolved];
		} while (typeof resolved === "string");
		if (typeof resolved === "object") return resolved;
	}

	const errorPlaceholderPart: Notice = {
		type: "notice",
		level: "WARNING",
		message: "Invalid part " + part,
	};
	return errorPlaceholderPart;
}


/**
 * Resolve part instance from it's reference
 */
export function componentOfPart(part: FormPartOrReference, partRef: PartRefFunction) {
	part = partRef(part); // resolve dynamic reference
	if (typeof part.type === "undefined") return EmptyPart;
	switch (part.type) {
		case "form":
			return FormPart;
		case "dialog":
			return DialogPart;
		case "custom":
			return (part as CustomPart).component;
		case "body":
			return BodyPart;
		case "block":
			return BlockPart;
		case "columns":
			return ColumnsPart;
		case "responsive":
			return ResponsivePart;
        case "touch":
            return TouchPart;
		case "widget":
			return WidgetPart;
		case "widgets":
			return WidgetsPart;
		case "widget-group":
			return WidgetGroupPart;
		case "datawidgets":
			return DataWidgetsPart;
		case "flow":
			return FlowPart;
		case "grid":
			return GridPart;
		case "split":
			return SplitPart;
		case "details":
			return DetailsPart;
		case "fieldset":
			return FieldsetPart;
		case "tabs":
			return TabsPart;
		case "array":
			return ArrayPart;
		case "items":
			return ItemsPart;
		case "agenda":
			return AgendaPart;
        case "masked":
            return MaskedPart;
        case "join-masked":
            return JoinMaskedPart;
        case "variant":
            return VariantPart;
        case "unmask":
            return UnmaskPart;
        case "choose":
            return ChoosePart;
        case "always-visible":
            return AlwaysVisiblePart;

		/* Widget parts */
		case "button":
			return ButtonPart;
		case "menu":
			return MenuPart;
		case "text":
			return TextPart;
		case "link":
			return LinkPart;
		case "html":
			return HtmlPart;
		case "icon":
			return IconPart;
		case "image":
			return ImagePart;
		case "consent":
			return ConsentPart;
		case "mod":
			return ModPart;

        case "input":
			return InputPart;
		case "textarea":
			return TextAreaPart;
		case "htmlarea":
			return HtmlAreaPart;
		case "checkbox":
			return CheckboxPart;
		case "radio":
			return RadioPart;
		case "select":
			return SelectPart;
		case "user-select":
			return UserSelectPart;
		case "cats":
			return CatsPart;
		case "checklist":
			return ChecklistPart;
		case "selection":
			return SelectionPart;
		case "usergroup":
			return UserGroupPart;
		case "suggest":
			return SuggesterPart;
		case "empty":
			return EmptyPart;
		case "temporal":
			return TemporalPart;
		case "datetime":
			return StaticDateTimePart;
		case "switch":
			return SwitchPart;
		case "money":
			return MoneyPart;
		case "eventcolor":
			return EventColorPart;
		case "filter":
			return FilterPart;
		case "filter-item":
			return FilterItemPart;
		case "view-columns":
			return ViewColumnsPart;
		case "mentions":
			return MentionsPart;
		case "file-entry":
			return FileEntryPart;
		case "file-zone":
			return FileZonePart;
		case "avatar":
			return AvatarPart;
		case "type-attach":
			return TypeAttachPart;
		case "intl-input":
			return IntlInputPart;
		case "intl-textarea":
			return IntlTextAreaPart;
		case "eaddress":
			return EAddressPart;
		case "address":
			return AddressPart;
		case "member-parties":
			return MemberPartiesPart;
		case "member-party":
			return MemberPartyPart;
		case "relations":
			return RelationsPart;
		case "relation":
			return RelationPart;
		case "notice":
			return NoticePart;

		/* packs view and editor */
		case "reference":
			return ReferencePart;
		/* multiple references */
		case "references":
			return ReferencesPart;
		/* One reference header */
		case "reference-header":
			return ReferenceHeaderPart;
		case "recurrence":
			return EventRecurrencePart;

		default:
			throw "Invalid part { type:" + part.type + " }";
	}
}

export const EMPTY_PART: Empty = { type: "empty" };
